/**
 * Wire protocol between the Noki Copilot panel and the noki-voice-copilot
 * server. Mirrors `noki_voice_copilot.protocol` (Python, pydantic).
 *
 * The protocol is JSON-over-WebSocket on the control plane (separate
 * from the WebRTC audio plane). All messages carry `protocol_version`
 * and a `type` discriminator.
 *
 * v3 (current) extends v2 additively with goal events that drive the
 * Workflow Card surface:
 *   server -> client: goal.update / goal.commit / goal.dismiss
 *   client -> server: goal.confirm / goal.cancel
 *
 * Field names match the WebMCP Chrome 146 W3C standard 1:1 so a future
 * shim that ingests WebMCP-declared tools can hand them to the bridge
 * with no renames.
 */

export const CONTROL_PROTOCOL_VERSION = "3";

const SERVER_MESSAGE_TYPES = new Set([
  "session.ready",
  "pong",
  "error",
  "tool.dispatch",
  "goal.update",
  "goal.commit",
  "goal.dismiss",
  // Governance banner (server -> client): the 80% heads-up + the final
  // wind-down reason. Additive on the wire; older bundles simply ignored it.
  "budget.notice",
]);

/**
 * Type guard for inbound server frames. Returns true if the value
 * minimally looks like one of our known server messages — full schema
 * validation is delegated to the consumer via the discriminator switch.
 */
export function isServerMessage(value) {
  if (!value || typeof value !== "object") return false;
  if (typeof value.type !== "string") return false;
  if (typeof value.protocol_version !== "string") return false;
  return SERVER_MESSAGE_TYPES.has(value.type);
}

/** Slot states the Workflow Card renders with governor opacity. */
export const SLOT_STATES = Object.freeze([
  "empty",
  "provisional",
  "confirmed",
  "locked",
]);

/** Inbound goal-event types (server -> client). */
export const GOAL_EVENT_TYPES = Object.freeze([
  "goal.update",
  "goal.commit",
  "goal.dismiss",
]);

export function isGoalEvent(value) {
  return Boolean(value) && GOAL_EVENT_TYPES.includes(value.type);
}
